/* Atypical Studio — theme script. No dependencies. */
(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const html = document.documentElement;
  const routes = (window.theme && window.theme.routes) || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const parse = (text) => new DOMParser().parseFromString(text, 'text/html');
  const debounce = (fn, ms) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };
  const store = {
    get(key) {
      try { return localStorage.getItem(key); } catch (e) { return null; }
    },
    set(key, value) {
      try { localStorage.setItem(key, value); } catch (e) { /* storage blocked */ }
    },
  };

  /* ---------- toast ---------- */
  let toastTimer;
  function toast(message) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-on'), 2600);
  }

  /* ---------- light / dark mode ---------- */
  function syncMode() {
    const mode = html.getAttribute('data-theme');
    const meta = $('[data-theme-color]');
    if (meta) meta.setAttribute('content', getComputedStyle(html).getPropertyValue('--bg').trim());
    $$('[data-mode-toggle]').forEach((btn) => {
      btn.setAttribute('aria-label', mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode');
      btn.setAttribute('aria-pressed', mode === 'dark' ? 'true' : 'false');
    });
  }
  function setMode(next, origin) {
    const apply = () => {
      html.setAttribute('data-theme', next);
      store.set('atp-mode', next);
      syncMode();
    };
    if (!document.startViewTransition || reduceMotion || !origin) return apply();
    const rect = origin.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const r = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
    const transition = document.startViewTransition(apply);
    transition.ready.then(() => {
      html.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${r}px at ${x}px ${y}px)`] },
        { duration: 650, easing: 'cubic-bezier(.2,.7,.2,1)', pseudoElement: '::view-transition-new(root)' }
      );
    });
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-mode-toggle]');
    if (!btn) return;
    setMode(html.getAttribute('data-theme') === 'dark' ? 'light' : 'dark', btn);
  });
  syncMode();

  /* ---------- header ---------- */
  const header = $('[data-header]');
  if (header) {
    const setHeight = () => html.style.setProperty('--hdr-h', `${header.offsetHeight}px`);
    setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(header);
    let lastY = scrollY;
    let ticking = false;
    const onScroll = () => {
      const y = scrollY;
      header.classList.toggle('is-scrolled', y > 24);
      const anyOpen = $('dialog[open]');
      if (!anyOpen) {
        const hide = y > 320 && y > lastY + 4;
        const show = y < lastY - 4 || y < 320;
        if (hide) header.classList.add('is-hidden');
        if (show) header.classList.remove('is-hidden');
        document.body.classList.toggle('hdr-hidden', header.classList.contains('is-hidden'));
      }
      lastY = y;
      ticking = false;
    };
    addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(onScroll);
      }
    }, { passive: true });
    onScroll();
  }

  /* ---------- dialogs (drawers, sheets) ---------- */
  function openDialog(dialog) {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    html.classList.add('is-locked');
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
    dialog.dispatchEvent(new CustomEvent('sheet:open'));
  }
  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.classList.remove('is-open');
    const done = () => {
      dialog.close();
      if (!$('dialog[open]')) html.classList.remove('is-locked');
    };
    if (reduceMotion || !dialog.classList.contains('sheet')) return done();
    setTimeout(done, 420);
  }
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      const id = opener.getAttribute('data-open');
      if (id === 'cart-drawer' && window.theme && window.theme.cartType === 'page') return;
      const dialog = document.getElementById(id);
      if (dialog) {
        e.preventDefault();
        openDialog(dialog);
      }
      return;
    }
    const closer = e.target.closest('[data-close]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }
    if (e.target.tagName === 'DIALOG' && e.target.open) closeDialog(e.target);
  });
  document.addEventListener('cancel', (e) => {
    if (e.target.tagName === 'DIALOG') {
      e.preventDefault();
      closeDialog(e.target);
    }
  }, true);

  /* ---------- cart ---------- */
  const cartDrawer = () => document.getElementById('cart-drawer');
  function updateCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', Number(count) === 0);
      el.classList.remove('is-bump');
      void el.offsetWidth;
      el.classList.add('is-bump');
    });
  }
  function renderDrawer(sectionHtml) {
    if (!sectionHtml) return;
    const doc = parse(sectionHtml);
    const next = $('[data-cart-root]', doc);
    const current = $('[data-cart-root]', cartDrawer() || document);
    if (next && current) current.innerHTML = next.innerHTML;
    const count = next && $('.sheet__count', next);
    if (count) updateCount(count.textContent.trim());
    initCards(current || document);
  }
  async function cartRequest(url, body) {
    const isForm = body instanceof FormData;
    const res = await fetch(url, {
      method: 'POST',
      headers: isForm ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: isForm ? body : JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Something went wrong');
    return data;
  }
  async function addToCart(payload, button) {
    if (button) button.classList.add('is-loading');
    try {
      let body = payload;
      if (body instanceof FormData) {
        body.append('sections', 'cart-drawer');
        body.append('sections_url', location.pathname);
      } else {
        body = { ...payload, sections: 'cart-drawer', sections_url: location.pathname };
      }
      const data = await cartRequest(`${routes.cartAdd}.js`, body);
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      if (window.theme && window.theme.cartType === 'page') {
        location.href = routes.cart;
        return;
      }
      renderDrawer(data.sections && data.sections['cart-drawer']);
      openDialog(cartDrawer());
    } catch (err) {
      toast(err.message);
    } finally {
      if (button) button.classList.remove('is-loading');
    }
  }
  async function changeLine(key, quantity, lineEl) {
    if (lineEl) lineEl.classList.add('is-busy');
    try {
      const data = await cartRequest(`${routes.cartChange}.js`, {
        id: key,
        quantity,
        sections: 'cart-drawer',
        sections_url: location.pathname,
      });
      renderDrawer(data.sections && data.sections['cart-drawer']);
      updateCount(data.item_count);
      const page = $('[data-cart-page]');
      if (page) {
        const doc = parse(await (await fetch(location.href)).text());
        const fresh = $('[data-cart-page]', doc);
        if (fresh) page.innerHTML = fresh.innerHTML;
      }
    } catch (err) {
      toast(err.message);
      if (lineEl) lineEl.classList.remove('is-busy');
    }
  }
  document.addEventListener('click', (e) => {
    const qtyBtn = e.target.closest('[data-line-qty]');
    if (qtyBtn) {
      const line = qtyBtn.closest('[data-line]');
      if (line) changeLine(line.getAttribute('data-line'), Number(qtyBtn.getAttribute('data-line-qty')), line);
      return;
    }
    const quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      addToCart({ items: [{ id: Number(quick.getAttribute('data-quick-add')), quantity: 1 }] }, quick);
      const card = quick.closest('[data-card]');
      if (card) card.classList.remove('is-quick');
      return;
    }
    const toggle = e.target.closest('[data-quick-toggle]');
    if (toggle) {
      e.preventDefault();
      const card = toggle.closest('[data-card]');
      $$('[data-card].is-quick').forEach((c) => c !== card && c.classList.remove('is-quick'));
      const on = finePointer ? card.classList.add('is-quick') || true : card.classList.toggle('is-quick');
      toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
      return;
    }
    if (!e.target.closest('[data-card]')) $$('[data-card].is-quick').forEach((c) => c.classList.remove('is-quick'));
  });

  /* ---------- product cards: swipe dots + hover quick add ---------- */
  function initCards(root = document) {
    $$('[data-card]', root).forEach((card) => {
      if (card.dataset.ready) return;
      card.dataset.ready = '1';
      if (finePointer) {
        const plus = $('[data-quick-toggle]', card);
        if (plus) {
          plus.addEventListener('mouseenter', () => card.classList.add('is-quick'));
          card.addEventListener('mouseleave', () => card.classList.remove('is-quick'));
        }
        return;
      }
      const swipe = $('[data-swipe]', card);
      const count = swipe ? swipe.children.length : 0;
      if (count < 2) return;
      const dots = document.createElement('span');
      dots.className = 'card__dots';
      dots.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < count; i++) dots.appendChild(document.createElement('i'));
      swipe.parentElement.appendChild(dots);
      let raf = 0;
      const update = () => {
        raf = 0;
        const k = Math.round(swipe.scrollLeft / (swipe.clientWidth || 1));
        Array.from(dots.children).forEach((d, n) => d.classList.toggle('on', n === k));
      };
      swipe.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
      update();
    });
  }

  /* ---------- rails ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-rail-prev], [data-rail-next]');
    if (!btn) return;
    const section = btn.closest('section');
    const rail = section && $('[data-rail]', section);
    if (!rail) return;
    const dir = btn.hasAttribute('data-rail-next') ? 1 : -1;
    rail.scrollBy({ left: dir * rail.clientWidth * 0.8, behavior: reduceMotion ? 'auto' : 'smooth' });
  });

  /* ---------- collection tabs ---------- */
  const tabCache = new Map();
  document.addEventListener('click', async (e) => {
    const tab = e.target.closest('[data-tab]');
    if (!tab || tab.classList.contains('is-on')) return;
    const wrap = tab.closest('[data-tabs]');
    const panel = $('[data-tabs-panel]', wrap);
    $$('[data-tab]', wrap).forEach((t) => {
      t.classList.toggle('is-on', t === tab);
      t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
    });
    const url = tab.getAttribute('data-tab');
    $$('[data-tabs-all]', wrap).forEach((a) => { a.href = url; });
    panel.classList.add('is-loading');
    try {
      if (!tabCache.has(url)) tabCache.set(url, await (await fetch(`${url}?view=rail`)).text());
      const doc = parse(tabCache.get(url));
      const limit = Number(wrap.getAttribute('data-limit')) || 8;
      panel.innerHTML = $$('.rail__item', doc).slice(0, limit).map((n) => n.outerHTML).join('');
      panel.scrollLeft = 0;
      initCards(panel);
    } catch (err) {
      toast('Could not load products');
    }
    panel.classList.remove('is-loading');
  });

  /* ---------- product page ---------- */
  function initProduct(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const variants = JSON.parse(($('[data-variants]', root) || {}).textContent || '[]');
    const form = $('[data-product-form]', root);
    const input = $('[data-variant-input]', root);
    const addBtn = $('[data-add]', root);
    const addLabel = $('[data-add-label]', root);
    const dynamic = $('[data-dynamic]', root);
    const fieldsets = $$('[data-option]', root);
    const sticky = $('[data-sticky-atc]', root);
    let current = variants.find((v) => input && String(v.id) === input.value) || null;
    if (!fieldsets.length && variants.length) current = variants[0];

    const selected = () => fieldsets.map((fs) => {
      const checked = $('input:checked', fs);
      return checked ? checked.value : null;
    });

    function markAvailability() {
      const sel = selected();
      fieldsets.forEach((fs, i) => {
        $$('input', fs).forEach((inp) => {
          const ok = variants.some((v) => v.available && v.options[i] === inp.value
            && sel.every((s, j) => j === i || s === null || v.options[j] === s));
          inp.classList.toggle('is-out', !ok);
        });
      });
    }

    function render() {
      const sel = selected();
      fieldsets.forEach((fs, i) => {
        const label = $('[data-option-value]', fs);
        if (label) label.textContent = sel[i] || '';
      });
      const complete = sel.every((s) => s !== null);
      current = complete ? variants.find((v) => v.options.every((o, i) => o === sel[i])) || null : current && !fieldsets.length ? current : null;
      if (input) input.value = current ? current.id : '';
      const shown = current || variants.find((v) => v.available) || variants[0];
      if (shown) {
        $$('[data-price-now]', root).forEach((el) => { el.textContent = shown.price; });
        const was = $('[data-price-was]', root);
        const off = $('[data-price-off]', root);
        if (was) { was.hidden = !shown.compare; was.textContent = shown.compare || ''; }
        if (off) { off.hidden = !shown.off; off.textContent = shown.off ? `−${shown.off}%` : ''; }
      }
      if (addBtn) {
        const soldOut = current && !current.available;
        addBtn.disabled = Boolean(soldOut);
        addBtn.classList.toggle('is-wait', !current);
        const missing = fieldsets.filter((fs, i) => sel[i] === null);
        const ask = missing.length === 1 ? `Select a ${missing[0].dataset.optionName}` : 'Select options';
        addLabel.textContent = !current ? (sel.length ? ask : 'Add to bag') : soldOut ? 'Sold out' : 'Add to bag';
      }
      if (dynamic) dynamic.classList.toggle('is-locked', !current || !current.available);
      if (current && complete) {
        const url = new URL(location.href);
        url.searchParams.set('variant', current.id);
        history.replaceState(history.state, '', url);
        if (current.media) scrollToMedia(current.media);
      }
      markAvailability();
    }

    function nudge() {
      const opts = $('[data-options]', root);
      if (!opts) return;
      opts.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      opts.classList.remove('opts-shake');
      void opts.offsetWidth;
      opts.classList.add('opts-shake');
      toast(fieldsets.length > 1 ? 'Pick your options first' : 'Pick a size first');
    }

    root.addEventListener('change', (e) => {
      if (e.target.closest('[data-option]')) render();
    });
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (!current) return nudge();
        if (!current.available) return;
        addToCart(new FormData(form), addBtn);
      });
    }

    // gallery
    const track = $('[data-gallery-track]', root);
    const counter = $('[data-gallery-index]', root);
    const bar = $('[data-gallery-bar]', root);
    function scrollToMedia(id) {
      const fig = track && $(`[data-media-id="${id}"]`, track);
      if (!fig || track.scrollWidth <= track.clientWidth) return;
      track.scrollTo({ left: fig.offsetLeft - track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    if (track && (counter || bar)) {
      let raf = 0;
      track.addEventListener('scroll', () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const i = Math.round(track.scrollLeft / (track.clientWidth || 1));
          if (counter) counter.textContent = i + 1;
          if (bar) bar.style.setProperty('--i', i);
        });
      }, { passive: true });
    }

    // zoom
    const zoom = $('dialog.zoom', root);
    root.addEventListener('click', (e) => {
      const z = e.target.closest('[data-zoom]');
      if (!z || !zoom) return;
      $$('img[data-src]', zoom).forEach((img) => { img.src = img.dataset.src; img.removeAttribute('data-src'); });
      zoom.showModal();
      html.classList.add('is-locked');
      const target = $$('img', zoom)[Number(z.getAttribute('data-zoom'))];
      if (target) requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
    });
    if (zoom) zoom.addEventListener('close', () => { if (!$('dialog[open]')) html.classList.remove('is-locked'); });

    // sticky bar
    const anchor = $('[data-atc-anchor]', root);
    if (sticky && anchor && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const on = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-on', on);
        document.body.classList.toggle('atc-on', on);
        sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
        const b = $('[data-sticky-add]', sticky);
        if (b) b.tabIndex = on ? 0 : -1;
      }).observe(anchor);
      $('[data-sticky-add]', sticky).addEventListener('click', () => {
        if (!current) return nudge();
        if (form) form.requestSubmit();
      });
    }

    // share
    const share = $('[data-share]', root);
    if (share) {
      share.addEventListener('click', async () => {
        const data = { title: share.dataset.title, url: share.dataset.url };
        try {
          if (navigator.share) await navigator.share(data);
          else { await navigator.clipboard.writeText(data.url); toast('Link copied'); }
        } catch (err) { /* share dismissed */ }
      });
    }

    if (addBtn && !current && fieldsets.length) addBtn.disabled = false;
    render();
  }

  /* ---------- recommendations ---------- */
  function initRecommendations(root = document) {
    $$('[data-recommendations]', root).forEach(async (el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      try {
        const doc = parse(await (await fetch(el.dataset.url)).text());
        const fresh = $('[data-recommendations]', doc);
        if (fresh && $('[data-card]', fresh)) {
          el.innerHTML = fresh.innerHTML;
          initCards(el);
        } else {
          el.closest('.shopify-section').hidden = true;
        }
      } catch (err) { /* leave empty */ }
    });
  }

  /* ---------- recently viewed ---------- */
  const VIEWED_KEY = 'atp-viewed';
  function readViewed() {
    try { return JSON.parse(localStorage.getItem(VIEWED_KEY)) || []; } catch (err) { return []; }
  }
  function initRecentlyViewed(root = document) {
    $$('[data-recently-viewed]', root).forEach(async (el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      const current = el.dataset.handle;
      const viewed = readViewed().filter((h) => h !== current);
      try { localStorage.setItem(VIEWED_KEY, JSON.stringify([current, ...viewed].slice(0, 12))); } catch (err) { /* storage blocked */ }

      const handles = viewed.slice(0, Number(el.dataset.limit) || 8);
      if (!handles.length) return;
      const base = (routes.root || '/').replace(/\/$/, '');
      const cards = await Promise.all(handles.map(async (h) => {
        try {
          const res = await fetch(`${base}/products/${encodeURIComponent(h)}?view=card`);
          return res.ok ? $('.rail__item', parse(await res.text())) : null;
        } catch (err) { return null; }
      }));
      const track = $('[data-rail]', el);
      cards.filter(Boolean).forEach((card) => track.appendChild(card));
      if (!track.children.length) return;
      el.hidden = false;
      initCards(el);
    });
  }

  /* ---------- collections: hover index & panels ---------- */
  function initCollectionIndex(root = document) {
    if (!finePointer) return;
    $$('[data-cindex]', root).forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      const float = $('[data-cindex-float]', el);
      const shots = $$('.cindex__shot', float);
      let x = 0, y = 0, cx = 0, cy = 0, raf = 0;
      const tick = () => {
        cx += (x - cx) * (reduceMotion ? 1 : 0.16);
        cy += (y - cy) * (reduceMotion ? 1 : 0.16);
        float.style.transform = `translate3d(${cx - float.offsetWidth / 2}px, ${cy - float.offsetHeight / 2}px, 0)`;
        raf = Math.abs(x - cx) + Math.abs(y - cy) > 0.5 ? requestAnimationFrame(tick) : 0;
      };
      el.addEventListener('pointermove', (e) => {
        x = e.clientX; y = e.clientY;
        if (!float.classList.contains('is-on')) { cx = x; cy = y; }
        if (!raf) raf = requestAnimationFrame(tick);
      });
      $$('[data-cindex-row]', el).forEach((row) => {
        row.addEventListener('pointerenter', () => {
          shots.forEach((s, i) => s.classList.toggle('is-on', i === Number(row.dataset.cindexRow)));
          float.classList.add('is-on');
        });
      });
      $('.cindex__list', el).addEventListener('pointerleave', () => float.classList.remove('is-on'));
    });
  }

  function initCollectionPanels(root = document) {
    $$('[data-cpanels]', root).forEach((track) => {
      const panels = $$('[data-cpanel]', track);
      const activate = (p) => panels.forEach((x) => x.classList.toggle('is-active', x === p));
      panels.forEach((p) => {
        p.addEventListener('pointerenter', () => finePointer && activate(p));
        p.addEventListener('focus', () => activate(p));
      });
    });
  }

  /* ---------- predictive search ---------- */
  const search = document.getElementById('search-sheet');
  if (search) {
    const input = $('[data-predictive-input]', search);
    const results = $('[data-predictive-results]', search);
    let controller;
    const run = debounce(async () => {
      const q = input.value.trim();
      if (!q) { results.innerHTML = ''; return; }
      if (controller) controller.abort();
      controller = new AbortController();
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        section_id: 'predictive-search',
      });
      try {
        const res = await fetch(`${routes.predictiveSearch}?${params}`, { signal: controller.signal });
        const doc = parse(await res.text());
        const section = $('#shopify-section-predictive-search', doc);
        results.innerHTML = section ? section.innerHTML : '';
      } catch (err) { /* aborted */ }
    }, 220);
    input.addEventListener('input', run);
    search.addEventListener('sheet:open', () => setTimeout(() => input.focus(), 60));
  }

  /* ---------- collection filters, sort, density, load more ---------- */
  function initPlp(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const sectionId = root.closest('.shopify-section').id.replace('shopify-section-', '');
    const results = () => $('[data-plp-results]', root);

    async function load(url, push = true) {
      const target = new URL(url, location.origin);
      target.searchParams.delete('section_id');
      const fetchUrl = new URL(target);
      fetchUrl.searchParams.set('section_id', sectionId);
      results().style.opacity = '0.5';
      try {
        const doc = parse(await (await fetch(fetchUrl)).text());
        const next = $('[data-plp-results]', doc);
        const current = results();
        const dialog = $('#filters', current);
        if (dialog && dialog.open) {
          ['.toolbar', '.active-filters', '.plp__count', '[data-plp-grid]', '.more'].forEach((sel) => {
            const a = $(sel, current);
            const b = $(sel, next);
            if (a && b) a.replaceWith(b);
            else if (a && !b) a.remove();
            else if (!a && b) $('[data-plp-grid]', current).insertAdjacentElement(sel === '.more' ? 'afterend' : 'beforebegin', b);
          });
          const body = $('.sheet__body', dialog);
          const nextBody = $('#filters .sheet__body', next);
          if (body && nextBody) {
            const scroll = body.scrollTop;
            body.innerHTML = nextBody.innerHTML;
            body.scrollTop = scroll;
          }
        } else {
          current.innerHTML = next.innerHTML;
        }
        initCards(current);
        bindSort();
        if (push) history.replaceState({}, '', target);
      } catch (err) {
        location.href = target;
      }
      results().style.opacity = '';
    }

    function formUrl() {
      const form = $('[data-facet-form]', root);
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => { if (value !== '') params.append(key, value); });
      const sort = $('[data-facet-sort]', root);
      if (sort && !params.has('sort_by')) params.set('sort_by', sort.value);
      return `${location.pathname}?${params}`;
    }
    const onFilter = debounce(() => load(formUrl()), 350);
    function bindSort() {
      const sort = $('[data-facet-sort]', root);
      if (sort && !sort.dataset.bound) {
        sort.dataset.bound = '1';
        sort.addEventListener('change', () => load(formUrl()));
      }
    }
    bindSort();
    root.addEventListener('change', (e) => { if (e.target.closest('[data-facet-form]')) onFilter(); });
    root.addEventListener('input', (e) => { if (e.target.matches('[data-facet-form] input[type="number"]')) onFilter(); });
    root.addEventListener('submit', (e) => { if (e.target.matches('[data-facet-form]')) { e.preventDefault(); load(formUrl()); } });
    root.addEventListener('click', async (e) => {
      const link = e.target.closest('[data-facet-link]');
      if (link) { e.preventDefault(); load(link.href); return; }
      const more = e.target.closest('[data-load-more]');
      if (more) {
        e.preventDefault();
        more.classList.add('is-loading');
        const url = new URL(more.href, location.origin);
        url.searchParams.set('section_id', sectionId);
        try {
          const doc = parse(await (await fetch(url)).text());
          const grid = $('[data-plp-grid]', root);
          const nextGrid = $('[data-plp-grid]', doc);
          Array.from(nextGrid.children).forEach((c) => grid.appendChild(c));
          initCards(grid);
          const box = more.closest('.more');
          const nextMore = $('.more', doc);
          if (nextMore) box.replaceWith(nextMore); else box.remove();
        } catch (err) { location.href = more.href; }
      }
      const density = e.target.closest('[data-density]');
      if (density) {
        const cols = density.getAttribute('data-density') === '2' ? 'big' : 'small';
        document.body.setAttribute('data-cols', cols);
        store.set('atp-cols', cols);
      }
    });
  }
  const savedCols = store.get('atp-cols');
  if (savedCols) document.body.setAttribute('data-cols', savedCols);

  /* ---------- reels ---------- */
  function initReels(root = document) {
    const vids = $$('[data-reel-video]', root).filter((v) => !v.dataset.ready);
    if (!vids.length) return;
    const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) {
          if (target.dataset.src && !target.src) target.src = target.dataset.src;
          if (!reduceMotion) target.play().catch(() => {});
        } else {
          target.pause();
        }
      });
    }, { threshold: 0.55 }) : null;
    vids.forEach((v) => {
      v.dataset.ready = '1';
      v.muted = true;
      if (io) io.observe(v);
      else if (v.dataset.src) v.src = v.dataset.src;
    });
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-reel-sound]');
    if (!btn) return;
    const reel = btn.closest('.reel');
    const video = $('video', reel);
    const loud = !reel.classList.contains('is-loud');
    $$('.reel.is-loud').forEach((r) => { r.classList.remove('is-loud'); const v = $('video', r); if (v) v.muted = true; });
    if (video) {
      video.muted = !loud;
      if (loud) video.play().catch(() => {});
    }
    reel.classList.toggle('is-loud', loud);
    btn.setAttribute('aria-label', loud ? 'Mute' : 'Unmute');
  });

  /* ---------- reveal ---------- */
  function initReveal(root = document) {
    const items = $$('.reveal:not(.is-in)', root);
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.forEach((el) => io.observe(el));
  }

  /* ---------- announcement rotate ---------- */
  $$('[data-rotate]').forEach((wrap) => {
    const msgs = $$('.announce__msg', wrap);
    if (msgs.length < 2 || reduceMotion) return;
    let i = 0;
    setInterval(() => {
      msgs[i].classList.remove('is-on');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('is-on');
    }, 4000);
  });

  /* ---------- boot ---------- */
  function init(root = document) {
    initCards(root);
    $$('[data-product]', root).forEach(initProduct);
    $$('[data-plp]', root).forEach(initPlp);
    initRecommendations(root);
    initRecentlyViewed(root);
    initCollectionIndex(root);
    initCollectionPanels(root);
    initReels(root);
    initReveal(root);
  }
  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  addEventListener('pageshow', (e) => {
    if (e.persisted) fetch(`${routes.cart}.js`).then((r) => r.json()).then((c) => updateCount(c.item_count)).catch(() => {});
  });
})();
